/* =====================================================================
 * Skill Store — 사용자 스킬 전역 상태 관리
 *
 * 업로드한 스킬 목록, 활성 스킬 선택, 모달 상태를 관리한다.
 * ===================================================================== */

import { create } from 'zustand';
import type { SkillSummary } from '@auw/api-client';
import {
  listSkills,
  uploadSkill,
  deleteSkill as deleteSkillApi,
  renameSkill as renameSkillApi,
  getSkillFavorites,
  setSkillFavorites,
} from '@auw/api-client';

interface SkillState {
  skills: SkillSummary[];
  activeSkillIds: string[];
  isModalOpen: boolean;
  modalInitialTab: 'my' | 'ref' | 'guide';
  isLoading: boolean;
  error: string | null;
  favoriteRefIds: string[];

  openModal: (tab?: 'my' | 'ref') => void;
  closeModal: () => void;

  fetchSkills: () => Promise<void>;
  uploadSkills: (files: File[]) => Promise<SkillSummary[]>;
  deleteSkill: (skillId: string) => Promise<void>;
  renameSkill: (skillId: string, newName: string) => Promise<void>;

  toggleSkill: (skillId: string) => void;
  setActiveSkillIds: (ids: string[]) => void;
  clearActiveSkills: () => void;

  fetchFavorites: () => Promise<void>;
  toggleFavorite: (refSkillId: string) => Promise<void>;
}

export const useSkillStore = create<SkillState>((set, get) => ({
  skills: [],
  activeSkillIds: [],
  isModalOpen: false,
  modalInitialTab: 'my',
  isLoading: false,
  error: null,
  favoriteRefIds: [],

  openModal: (tab: 'my' | 'ref' | 'guide' = 'my') => set({ isModalOpen: true, modalInitialTab: tab }),
  closeModal: () => set({ isModalOpen: false }),

  fetchSkills: async () => {
    set({ isLoading: true, error: null });
    try {
      const result = await listSkills();
      set({ skills: result.skills, isLoading: false });
    } catch (err) {
      set({ error: String(err), isLoading: false });
    }
  },

  uploadSkills: async (files: File[]) => {
    set({ isLoading: true, error: null });
    try {
      const result = await uploadSkill(files);
      if (result.errors?.length) {
        set({ error: result.errors.join('\n') });
      }
      await get().fetchSkills();
      set({ isLoading: false });

      return result.skills;
    } catch (err) {
      set({ error: String(err), isLoading: false });
      return [];
    }
  },

  deleteSkill: async (skillId: string) => {
    try {
      await deleteSkillApi(skillId);
      set((s) => ({
        skills: s.skills.filter((sk) => sk.skill_id !== skillId),
        activeSkillIds: s.activeSkillIds.filter((id) => id !== skillId),
      }));
    } catch (err) {
      set({ error: String(err) });
    }
  },

  renameSkill: async (skillId: string, newName: string) => {
    try {
      await renameSkillApi(skillId, newName);
      set((s) => ({
        skills: s.skills.map((sk) =>
          sk.skill_id === skillId ? { ...sk, name: newName } : sk,
        ),
      }));
    } catch (err) {
      set({ error: String(err) });
    }
  },

  toggleSkill: (skillId: string) =>
    set((s) => ({
      activeSkillIds: s.activeSkillIds.includes(skillId) ? [] : [skillId],
    })),

  setActiveSkillIds: (ids: string[]) => set({ activeSkillIds: ids }),
  clearActiveSkills: () => set({ activeSkillIds: [] }),

  fetchFavorites: async () => {
    try {
      const ids = await getSkillFavorites();
      set({ favoriteRefIds: ids });
    } catch {
      /* silent */
    }
  },

  toggleFavorite: async (refSkillId: string) => {
    const prev = get().favoriteRefIds;
    const next = prev.includes(refSkillId)
      ? prev.filter((id) => id !== refSkillId)
      : [...prev, refSkillId];
    set({ favoriteRefIds: next });
    try {
      await setSkillFavorites(next);
    } catch {
      set({ favoriteRefIds: prev });
    }
  },
}));
